import { useMemo, useState } from "react";
import { Button, TextInput } from "@a2z/ui";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { GOVERNORATES } from "../governorates";
import {
  GovernorateGrid,
  Icons,
  Label,
  ModalShell,
  tokens,
} from "../primitives";

export type ShippingRule = {
  id: string | number;
  name: string;
  fee: number;
  days: number;
  zones: string[];
  active: boolean;
};

export function ShippingRuleModal({
  rule,
  governorates,
  coveredZones,
  onClose,
  onSave,
}: {
  rule: ShippingRule | null;
  governorates?: string[];
  coveredZones: Set<string>;
  onClose: () => void;
  onSave: (r: Omit<ShippingRule, "id" | "active">) => void;
}) {
  const t = useTranslations();
  const ss = keys.seller.storeSettings;
  const allGovernorates =
    governorates && governorates.length > 0 ? governorates : GOVERNORATES;
  const [name, setName] = useState(rule?.name ?? "");
  const [fee, setFee] = useState(rule?.fee?.toString() ?? "");
  const [days, setDays] = useState(rule?.days?.toString() ?? "");
  const [zones, setZones] = useState(new Set<string>(rule?.zones ?? []));
  const [search, setSearch] = useState("");

  const filtered = useMemo(
    () =>
      allGovernorates.filter((g) =>
        g.toLowerCase().includes(search.toLowerCase()),
      ),
    [allGovernorates, search],
  );
  const toggleZone = (z: string) => {
    if (coveredZones.has(z)) return;
    const next = new Set(zones);
    if (next.has(z)) next.delete(z);
    else next.add(z);
    setZones(next);
  };
  const allSelectableSelected = filtered
    .filter((g) => !coveredZones.has(g))
    .every((g) => zones.has(g));
  const toggleAll = () => {
    const next = new Set(zones);
    const selectable = filtered.filter((g) => !coveredZones.has(g));
    if (allSelectableSelected) selectable.forEach((g) => next.delete(g));
    else selectable.forEach((g) => next.add(g));
    setZones(next);
  };

  const valid = Boolean(name.trim()) && Boolean(fee) && Boolean(days) && zones.size > 0;
  const save = () =>
    onSave({
      name: name.trim(),
      fee: parseFloat(fee),
      days: parseInt(days, 10),
      zones: Array.from(zones),
    });

  return (
    <ModalShell
      icon={Icons.truck}
      title={rule ? t(ss.editShippingRule) : t(ss.newShippingRule)}
      subtitle={t(ss.shippingRuleSubtitle)}
      onClose={onClose}
      footerHint={t(ss.governoratesSelected, {
        count: zones.size,
        total: allGovernorates.length,
      })}
      onSave={save}
      valid={valid}
      saveLabel={rule ? t(ss.saveChanges) : t(ss.addShippingRule)}
      saveIcon={!rule}
    >
      <div className="grid grid-cols-2 gap-3 sm:[grid-template-columns:1.4fr_1fr_1fr]">
        <div className="col-span-2 sm:col-span-1">
          <Label>
            {t(ss.ruleName)} <span style={{ color: tokens.danger }}>*</span>
          </Label>
          <div className="mt-1">
            <TextInput
              name="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t(ss.ruleNamePlaceholder)}
            />
          </div>
        </div>
        <div>
          <Label>
            {t(ss.shippingFeeLabel)}{" "}
            <span style={{ color: tokens.danger }}>*</span>
          </Label>
          <div className="mt-1">
            <TextInput
              name="fee"
              type="number"
              value={fee}
              onChange={(e) => setFee(e.target.value)}
              placeholder="20"
              className="font-mono"
              suffixSpace={48}
              suffix={
                <span
                  className="font-mono"
                  style={{ fontSize: 12, fontWeight: 600, color: tokens.text3 }}
                >
                  {t(ss.currencyEgp)}
                </span>
              }
            />
          </div>
        </div>
        <div>
          <Label>
            {t(ss.deliveryTimeLabel)}{" "}
            <span style={{ color: tokens.danger }}>*</span>
          </Label>
          <div className="mt-1">
            <TextInput
              name="days"
              type="number"
              value={days}
              onChange={(e) => setDays(e.target.value)}
              placeholder="2"
              className="font-mono"
              suffixSpace={48}
              suffix={
                <span
                  className="font-mono"
                  style={{ fontSize: 12, fontWeight: 600, color: tokens.text3 }}
                >
                  {t(ss.daysUnit)}
                </span>
              }
            />
          </div>
        </div>
      </div>

      <div style={{ marginTop: 18 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 8,
          }}
        >
          <Label>
            {t(ss.coveredGovernorates)}{" "}
            <span style={{ color: tokens.danger }}>*</span>
          </Label>
          <Button variant="outline" size="sm" onClick={toggleAll}>
            {allSelectableSelected ? t(ss.deselectAll) : t(ss.selectAll)}
          </Button>
        </div>
        <div className="mb-2.5">
          <TextInput
            name="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t(ss.searchGovernorates)}
            prefixSpace={34}
            prefix={Icons.search}
          />
        </div>
        <GovernorateGrid
          governorates={filtered}
          selected={zones}
          onToggle={toggleZone}
          disabledCovered={coveredZones}
          height={280}
        />
        <div style={{ marginTop: 6, fontSize: 11, color: tokens.text3 }}>
          {t(ss.greyedCovered)}
        </div>
      </div>
    </ModalShell>
  );
}
